import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { SectionLabel, SimulatedTag } from '@hive/ui';
import { api, errorMessage, type OrderStatus, type VenueOrder } from '../api/client';
import { useNow, useOrders, useVenues } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { VenueHeader } from '../components/VenueHeader';
import { venueTime } from '../lib/format';

/** Minutes waiting before an open order is flagged. */
const SLOW_MINUTES = 10;

/**
 * The venue's Nectar order queue: orders from the patron page arrive live; staff move
 * each one on (new, preparing, served) or cancel it. Tablet first.
 */
export function OrdersPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { venues, error: venuesError } = useVenues();
  const now = useNow(15_000);
  const venueId = params.get('venue') ?? venues?.find((v) => v.source === 'real')?.id ?? venues?.[0]?.id;
  const venue = venues?.find((v) => v.id === venueId);
  const allowed = Boolean(user?.permissions.includes('orders.manage'));
  const { orders, offline, live, apply } = useOrders(venue?.id, allowed);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (user && !allowed) return <Navigate to="/" replace />;

  async function move(order: VenueOrder, status: Exclude<OrderStatus, 'new'>) {
    if (!venue) return;
    setBusy(`${order.id}:${status}`);
    setError(null);
    try {
      const { data, error: err } = await api.PATCH('/v1/venues/{venueId}/orders/{orderId}', {
        params: { path: { venueId: venue.id, orderId: order.id } },
        body: { status },
      });
      if (data) apply(data);
      else setError(errorMessage(err));
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(null);
    }
  }

  const fresh = orders?.open.filter((o) => o.status === 'new') ?? [];
  const preparing = orders?.open.filter((o) => o.status === 'preparing') ?? [];

  return (
    <main className="hv-root k-page">
      {venue && (
        <VenueHeader
          name={venue.name}
          context="Nectar orders"
          extra={
            venues && venues.length > 1 ? (
              <label className="k-venue-switch">
                <span className="hv-sr-only">Venue</span>
                <select value={venue.id} onChange={(e) => navigate(`/orders?venue=${encodeURIComponent(e.target.value)}`)}>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : undefined
          }
        />
      )}
      {venuesError && <p className="k-inline-error">Could not load your venues. Refresh the page to try again.</p>}
      {offline && <p className="k-inline-warn">Connection lost. Showing the last known orders.</p>}
      {error && (
        <p className="k-inline-error" role="alert">
          {error}
        </p>
      )}

      <div className="k-orders__bar">
        <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm" onClick={() => navigate(venue ? `/?venue=${venue.id}` : '/')}>
          Back to screens
        </button>
        <span className="k-small hv-muted">
          <span className={`hv-dot hv-dot--sm ${live ? 'hv-dot--live' : 'hv-dot--idle'}`} /> {live ? 'Live: new orders appear here as they are placed' : 'Reconnecting…'}
        </span>
        <span className="k-small hv-muted">
          Menu <SimulatedTag title="The menu is a demo list: no till or kitchen system is connected" />
        </span>
      </div>

      {!orders ? (
        <p className="hv-muted">Loading orders…</p>
      ) : (
        <div className="k-orders">
          <OrderColumn title="New" empty="No new orders." list={fresh} now={now} busy={busy} onMove={move} />
          <OrderColumn title="Preparing" empty="Nothing being prepared." list={preparing} now={now} busy={busy} onMove={move} />
          <section aria-label="Done today" className="k-orders__done">
            <SectionLabel>Done today</SectionLabel>
            {orders.done.length === 0 ? (
              <p className="hv-muted k-small">Served and cancelled orders since opening appear here.</p>
            ) : (
              <ul className="k-orders__done-list">
                {orders.done.map((o) => (
                  <li key={o.id} className={o.status === 'cancelled' ? 'k-orders__cancelled' : undefined}>
                    <span className="k-order__table-sm">Table {o.tableNo}</span>
                    <span className="k-small">{summary(o)}</span>
                    <span className="k-small hv-muted">
                      {o.status === 'served' ? 'Served' : 'Cancelled'} {o.updatedAt ? venueTime(o.updatedAt, venue?.timezone) : ''}
                      {o.updatedBy ? ` · ${o.updatedBy}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </main>
  );
}

function OrderColumn({
  title,
  empty,
  list,
  now,
  busy,
  onMove,
}: {
  title: string;
  empty: string;
  list: VenueOrder[];
  now: number;
  busy: string | null;
  onMove(order: VenueOrder, status: Exclude<OrderStatus, 'new'>): void;
}) {
  return (
    <section aria-label={title} className="k-orders__col">
      <SectionLabel>
        {title} · {list.length}
      </SectionLabel>
      {list.length === 0 ? <p className="hv-muted k-small">{empty}</p> : list.map((o) => <OrderCard key={o.id} order={o} now={now} busy={busy} onMove={onMove} />)}
    </section>
  );
}

function OrderCard({ order, now, busy, onMove }: { order: VenueOrder; now: number; busy: string | null; onMove(order: VenueOrder, status: Exclude<OrderStatus, 'new'>): void }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const minutes = Math.max(0, Math.floor((now - Date.parse(order.placedAt)) / 60_000));
  const slow = minutes >= SLOW_MINUTES;
  const working = busy?.startsWith(`${order.id}:`) ?? false;
  return (
    <article className={`hv-card k-order k-order--${order.status}`} aria-label={`Table ${order.tableNo}, ${order.status}`}>
      <div className="k-order__head">
        <span className="k-order__table">Table {order.tableNo}</span>
        <span className={`k-small${slow ? ' k-order__slow' : ' hv-muted'}`}>{minutes < 1 ? 'Just now' : `${minutes} min ago`}</span>
      </div>
      <ul className="k-order__items">
        {order.items.map((i) => (
          <li key={i.menuItemId}>
            <span className="k-order__qty">{i.quantity} ×</span> {i.name}
          </li>
        ))}
      </ul>
      {(order.screenCode || order.updatedBy) && (
        <span className="k-small hv-muted">
          {[order.screenCode && `Scanned at screen ${order.screenCode}`, order.status === 'preparing' && order.updatedBy && `Started by ${order.updatedBy}`].filter(Boolean).join(' · ')}
        </span>
      )}
      <div className="k-order__actions">
        {confirmCancel ? (
          <>
            <button type="button" className="hv-btn hv-btn--secondary" disabled={working} onClick={() => onMove(order, 'cancelled')}>
              {busy === `${order.id}:cancelled` ? 'Cancelling…' : 'Yes, cancel order'}
            </button>
            <button type="button" className="hv-btn hv-btn--link" onClick={() => setConfirmCancel(false)}>
              Keep it
            </button>
          </>
        ) : (
          <>
            {order.status === 'new' && (
              <button type="button" className="hv-btn hv-btn--primary" disabled={working} onClick={() => onMove(order, 'preparing')}>
                {busy === `${order.id}:preparing` ? 'Starting…' : 'Start preparing'}
              </button>
            )}
            <button type="button" className={`hv-btn ${order.status === 'preparing' ? 'hv-btn--primary' : 'hv-btn--secondary'}`} disabled={working} onClick={() => onMove(order, 'served')}>
              {busy === `${order.id}:served` ? 'Saving…' : 'Served'}
            </button>
            <button type="button" className="hv-btn hv-btn--link" disabled={working} onClick={() => setConfirmCancel(true)}>
              Cancel
            </button>
          </>
        )}
      </div>
    </article>
  );
}

function summary(o: VenueOrder): string {
  return o.items.map((i) => `${i.quantity} × ${i.name}`).join(', ');
}
